/**
 * A student's face on a roster row, or their initials when there is no photo on file.
 *
 * Most students will have no headshot for a long time after this ships, so the initials state
 * is the normal state, not an error state — it is styled to look deliberate rather than
 * broken, and the tinted background gives the eye something stable to scan down a long list.
 */

import { useEffect, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent, MouseEvent as ReactMouseEvent } from 'react';
import { nameTokens } from '../utils/studentPhotoName';

/**
 * How far a finger may travel and still count as a tap rather than a swipe.
 *
 * The roster row this sits on is swipeable, and a horizontal drag that starts on the avatar
 * still fires a click on touchend. Without this the enlarged photo would spring open every
 * time a teacher swiped a row to check someone in.
 */
const TAP_SLOP_PX = 10;

export type AvatarSize = 'sm' | 'md' | 'lg';

const SIZE_CLASSES: Record<AvatarSize, string> = {
  sm: 'h-8 w-8 text-[11px]',
  md: 'h-10 w-10 text-xs',
  lg: 'h-16 w-16 text-lg',
};

/**
 * Tints for the initials state. Muted on purpose: a roster row's own badges (allergy red,
 * Flex amber) carry meaning, and an avatar colour that competes with them would be read as
 * signal when it is only decoration.
 */
const TINTS = [
  'bg-slate-100 text-slate-600',
  'bg-blue-50 text-blue-700',
  'bg-emerald-50 text-emerald-700',
  'bg-violet-50 text-violet-700',
  'bg-amber-50 text-amber-700',
  'bg-rose-50 text-rose-700',
  'bg-cyan-50 text-cyan-700',
] as const;

/** First letter of the first and last name parts — "Ana Sofía Núñez" reads as "AN". */
export function initialsFor(name: string): string {
  const tokens = nameTokens(name);
  if (tokens.length === 0) return '?';
  const first = tokens[0][0];
  const last = tokens.length > 1 ? tokens[tokens.length - 1][0] : '';
  return (first + last).toUpperCase();
}

/** Stable per-name tint, so a child keeps the same colour every day. */
export function tintFor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return TINTS[hash % TINTS.length];
}

/**
 * Saturated fills for the parent portal, where an initials circle is the whole avatar.
 *
 * Distinct from `TINTS` above by intent, not taste: on a teacher's roster the avatar sits
 * beside allergy and Flex badges that carry meaning, so it stays pale enough to read as
 * decoration. On a parent's own dashboard there are only ever two or three children and the
 * circle's job is the opposite — to be the thing the eye locks onto when scanning for
 * "which of mine is this".
 */
const SOLID_TINTS = [
  'bg-blue-500', 'bg-emerald-500', 'bg-violet-500', 'bg-rose-500',
  'bg-amber-500', 'bg-cyan-600', 'bg-pink-500', 'bg-teal-500',
] as const;

/**
 * Stable per-name fill for the parent portal's initials circles.
 *
 * The hash is deliberately the one the Students page has always used, rather than the
 * cleaner one above: changing it would repaint every child the day this ships, and a parent
 * who has learned that Harper is the pink one should not have to relearn it.
 */
export function solidTintFor(name: string): string {
  let h = 0;
  for (const c of name) h = ((h << 5) - h + c.charCodeAt(0)) & 0xffff;
  return SOLID_TINTS[Math.abs(h) % SOLID_TINTS.length];
}

interface Props {
  name: string;
  /** Signed URL, or null/undefined when there is no headshot on file. */
  photoUrl?: string | null;
  size?: AvatarSize;
  className?: string;
  /**
   * Called when the avatar is tapped. Only wired up when there is actually a photo to
   * enlarge — enlarging an initials circle would be a dead end, so without a photo the tap
   * falls through to whatever owns the row.
   */
  onEnlarge?: () => void;
}

export function StudentAvatar({ name, photoUrl, size = 'md', className = '', onEnlarge }: Props) {
  const [failed, setFailed] = useState(false);
  const pressedAt = useRef<{ x: number; y: number } | null>(null);
  /** Whether the last pointer interaction was a swipe rather than a tap. */
  const dragged = useRef(false);

  // A new URL deserves a fresh attempt: signed URLs are re-issued on expiry, and staying
  // failed would leave the face missing for the rest of the session.
  useEffect(() => setFailed(false), [photoUrl]);

  const base = `${SIZE_CLASSES[size]} flex-shrink-0 rounded-full overflow-hidden`;

  if (photoUrl && !failed) {
    const image = (
      <img
        src={photoUrl}
        alt={name}
        loading="lazy"
        decoding="async"
        onError={() => setFailed(true)}
        className={`${base} object-cover bg-gray-100 ${className}`}
      />
    );

    if (!onEnlarge) return image;

    return (
      <button
        type="button"
        aria-label={`Enlarge photo of ${name}`}
        // Pointer events rather than click: they fire for both mouse and touch, and give the
        // start position needed to tell a tap from the row's swipe gesture.
        onPointerDown={(e: ReactPointerEvent) => {
          pressedAt.current = { x: e.clientX, y: e.clientY };
          dragged.current = false;
        }}
        onPointerUp={(e: ReactPointerEvent) => {
          const from = pressedAt.current;
          pressedAt.current = null;
          if (!from) return;
          dragged.current = Math.hypot(e.clientX - from.x, e.clientY - from.y) > TAP_SLOP_PX;
        }}
        // Click is the trigger, not pointerup, because keyboard activation (Enter/Space)
        // raises a click with no pointer events at all — driving this from pointerup would
        // leave the enlarged photo unreachable without a mouse or a finger. The pointer
        // handlers above only decide whether a click that follows a swipe should count.
        onClick={(e: ReactMouseEvent) => {
          // The row underneath treats a bare tap as "open the swipe tray". Swallow the click
          // so enlarging a photo does not also fling the actions out.
          e.stopPropagation();
          if (dragged.current) {
            dragged.current = false;
            return;
          }
          onEnlarge();
        }}
        className={`${SIZE_CLASSES[size]} flex-shrink-0 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500`}
      >
        {image}
      </button>
    );
  }

  return (
    <div
      // Decorative once the name is already on the row; announcing initials would have a
      // screen reader read every child's name twice.
      aria-hidden="true"
      className={`${base} ${tintFor(name)} flex items-center justify-center font-semibold ${className}`}
    >
      {initialsFor(name)}
    </div>
  );
}
